/**
 * SACP Increment 3 UI — shared, PURE display-contract helpers.
 *
 * These encode the owner display contracts so both the SuperAdmin and tenant surfaces render them
 * identically, and so they are unit-testable without a DOM:
 *   - names, never raw IDs, as primary labels
 *   - exact USD from integer cents
 *   - explicit "N used of Unlimited" (never "unlimited up to 130")
 *   - grandfather + allowance-source labels
 *
 * Backend is the source of truth; these only FORMAT already-resolved backend fields.
 */

export const UNLIMITED = -1;

/** USD from integer cents. null/undefined -> em dash. */
export function usd(cents) {
  if (cents === null || cents === undefined) return '—';
  const dollars = Number(cents) / 100;
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Annual price given a monthly cents value using the accepted "pay 10, get 12" discount. */
export function usdAnnual(monthlyCents, monthsPaid = 10) {
  if (monthlyCents === null || monthlyCents === undefined) return '—';
  return usd(monthlyCents * monthsPaid);
}

/** A single limit value -> exact human string. -1 => "Unlimited"; never a fabricated ceiling. */
export function limitValue(v) {
  if (v === UNLIMITED || v === 'unlimited' || v === 'Unlimited') return 'Unlimited';
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString();
}

/** Usage line: "8 of 10 used" for finite; "11 used of Unlimited" for -1. */
export function usageLine(used, limit) {
  const u = Number(used || 0).toLocaleString();
  if (limit === UNLIMITED || limit === 'unlimited' || limit === 'Unlimited') {
    return `${u} used of Unlimited`;
  }
  if (limit === null || limit === undefined) return `${u} used`;
  return `${u} of ${Number(limit).toLocaleString()} used`;
}

/** Remaining capacity, honoring Unlimited. */
export function remaining(used, limit) {
  if (limit === UNLIMITED || limit === 'unlimited') return 'Unlimited';
  if (limit === null || limit === undefined) return '—';
  return Math.max(0, Number(limit) - Number(used || 0)).toLocaleString();
}

/** Plan + version label, e.g. "Professional v2.0" — never "professional@v2.0". */
export function planVersionLabel(planDisplayName, version) {
  const v = (version || '').toString().trim();
  const vv = v && !v.toLowerCase().startsWith('v') ? `v${v}` : v;
  return `${planDisplayName || 'Unknown plan'} ${vv}`.trim();
}

/** Status label from a grandfathered flag / subscription status. */
export function statusLabel({ grandfathered, subscription_status } = {}) {
  if (grandfathered) return 'Grandfathered';
  const map = {
    trial: 'Trial', active: 'Active', grace: 'In Grace', past_due: 'Past Due',
    limited: 'Limited', cancelled: 'Cancelled',
  };
  return map[subscription_status] || 'Active';
}

/** Business-plane feature state -> {label, tone} for a badge/pill. Honest, non-actionable states included. */
export function featureStateLabel(state) {
  const map = {
    AVAILABLE: { label: 'Available', tone: 'green' },
    CONFIGURE_REQUIRED: { label: 'Configure', tone: 'blue' },
    LOCKED: { label: 'Locked', tone: 'gray' },
    IN_DEVELOPMENT: { label: 'In Development', tone: 'amber' },
    PRIVATE_BETA_LOCKED: { label: 'Private Beta', tone: 'purple' },
    OPERATIONALLY_DISABLED: { label: 'Temporarily Unavailable', tone: 'red' },
    ROLE_DENIED: { label: 'Not Permitted', tone: 'gray' },
  };
  return map[state] || { label: state || 'Unknown', tone: 'gray' };
}

/** Action label for a locked feature, using availability_mode/action from the backend. */
export function lockedActionLabel(action, addOnPriceCents) {
  switch (action) {
    case 'add_on':
      return addOnPriceCents != null ? `Add for ${usd(addOnPriceCents)}/mo` : 'Add-on required';
    case 'upgrade': return 'Upgrade plan';
    case 'request_access': return 'Request access';
    case 'contact_servisfy': return 'Contact Servisfy';
    default: return null;
  }
}

/**
 * RAW-ID GUARD (measurement helper). Given an object destined for a primary label position,
 * returns true if it looks like a raw internal ID rather than a human name. Used by tests to
 * assert RAW_ID_PRIMARY_DISPLAY_COUNT=0 for our rendered rows.
 */
const RAW_ID_RE = /^(st_|u_|u-|tcs_|csr_|offer_|gf_|[0-9a-f]{32}$|[0-9a-f]{8}-[0-9a-f]{4}-)/;
export function looksLikeRawId(s) {
  if (typeof s !== 'string') return false;
  if (/@v\d/.test(s)) return true;                 // professional@v2.0
  return RAW_ID_RE.test(s);
}

/** Pick a safe primary label: prefer the resolved display name, never fall back to a raw id. */
export function primaryLabel(displayName, fallback = 'Unknown') {
  if (displayName && !looksLikeRawId(displayName)) return displayName;
  return fallback;
}

/**
 * Extract a list from a backend response that may be wrapped by paginate() (`{items:[...]}`),
 * a named key (`{features:[...]}`), or be a bare array. The commercial owner endpoints for
 * features/integrations use paginate() → `.items`; reading `.features`/`.integrations` returned
 * empty ("No features configured") — this is the fix.
 */
export function listFrom(data) {
  if (Array.isArray(data)) return data;
  return (data && (data.items || data.features || data.integrations || data.plans
    || data.add_ons || data.usage_packs)) || [];
}

/**
 * Build a tenant-override request body. The backend create_override REQUIRES `commercial_basis`
 * ∈ complimentary|promotional|contract|checkout_required (a paid override must be classified — no
 * silent charge); omitting it (or sending `source`) returns 422 COMMERCIAL_BASIS_REQUIRED.
 */
export function overrideBody({ type, limitKey, value, reason, commercialBasis = 'complimentary' }) {
  return type === 'deny'
    ? { type: 'deny', feature_key: limitKey, reason, commercial_basis: commercialBasis }
    : { type: 'limit_override', limit_key: limitKey, value, reason, commercial_basis: commercialBasis };
}
